/**
 * GiftClaimPage.jsx — /gifts/:code — recipient's gift-activation flow.
 *
 * Public route (no auth required to view). Renders:
 *   • Sender name + gift message
 *   • Vendor + item + face value
 *   • "You must be a HustleSuite member using {recipient_email} to claim"
 *   • T&C checkbox
 *   • Claim button → POST /api/vouchers/gift/accept
 *
 * If the recipient isn't logged in, we prompt them to sign in/up
 * with the matching email first. The activation POST is auth-gated
 * so the frontend redirects to /login?next=/gifts/{code} when the
 * server responds 401.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Gift, CheckCircle2, ExternalLink, ShieldCheck } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function fmtMoney(cents = 0, cur = "AUD") {
  return `$${(cents / 100).toFixed(2)} ${cur}`;
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function GiftClaimPage() {
  const { code } = useParams();
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const [gift, setGift] = useState(null);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = "Claim your gift — HustleSuite";
    (async () => {
      try {
        const { data } = await axios.get(`${API}/vouchers/gift/${code}`);
        setGift(data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
    })();
  }, [code, i18n.language]);

  const claim = async () => {
    if (!accepted) return;
    setClaiming(true);
    try {
      const { data } = await axios.post(
        `${API}/vouchers/gift/accept`,
        { code, accept_terms: true },
        { headers: authHeader(), withCredentials: true },
      );
      toast.success(t("gift_claimed", { defaultValue: "Gift activated!" }));
      setClaimed(true);
      setGift((g) => ({ ...g, status: data.status }));
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) {
        toast.info(t("gift_login_required", {
          defaultValue: "Please sign in with the gift's email to claim it.",
        }));
        nav(`/login?next=/gifts/${gift?.code || ""}`);
        return;
      }
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div
      data-testid="gift-claim-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleSuite", to: "/" }}
        showBookCta={false}
        showCart={false}
      />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        {error && (
          <div className="rounded-xl border border-red-300 bg-red-50 text-red-800 px-4 py-3 mb-6"
            data-testid="gift-error">{error}</div>
        )}
        {gift && (
          <>
            <div className="text-center mb-8">
              <div className="mx-auto w-20 h-20 rounded-2xl bg-amber-400/10 flex items-center justify-center">
                <Gift size={36} className="text-amber-500"/>
              </div>
              <p className="mt-6 text-amber-500 text-[11px] font-bold uppercase tracking-[0.32em]">
                {t("gift_kicker", { defaultValue: "You've received a gift" })}
              </p>
              <h1 className="mt-2 text-3xl md:text-4xl font-black">
                {t("gift_title", { defaultValue: "For you," })}
                {" "}<span className="text-amber-500">{gift.recipient_name || ""}</span>
              </h1>
              <p className="mt-3 text-sm text-stone-500">
                {t("gift_from", { defaultValue: "From" })}{" "}
                <strong>{gift.purchaser_email}</strong>
              </p>
            </div>

            <div className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 mb-6">
              <p className="text-[11px] uppercase tracking-widest text-stone-400 font-bold">
                {gift.vendor_name}
              </p>
              <h2 className="mt-1 font-black text-xl">{gift.item_description}</h2>
              <p className="mt-3 text-2xl font-black text-amber-500">
                {fmtMoney(gift.face_value_cents, gift.currency)}
              </p>
              <p className="text-xs text-stone-500 mt-1">
                {t("gift_valid_until", { defaultValue: "Valid until" })} {fmtDate(gift.valid_until)}
              </p>
              {gift.gift_message && (
                <blockquote
                  data-testid="gift-message"
                  className="mt-5 pl-4 border-l-4 border-amber-500 italic text-stone-600 dark:text-stone-300"
                >
                  “{gift.gift_message}”
                </blockquote>
              )}
            </div>

            {claimed || gift.status === "active" || gift.status === "redeemed" ? (
              <div className="rounded-2xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 dark:border-emerald-800 p-6 text-center"
                data-testid="gift-claimed">
                <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3"/>
                <h3 className="font-black text-xl">
                  {t("gift_activated_title", { defaultValue: "Gift activated!" })}
                </h3>
                <p className="text-sm text-stone-600 mt-2">
                  {t("gift_activated_sub", {
                    defaultValue: "Head to your account to see this voucher and redeem it at the venue.",
                  })}
                </p>
                <Link to="/account/ledger"
                  data-testid="gift-view-vouchers"
                  className="mt-4 inline-flex items-center gap-2 h-11 px-6 rounded-full text-sm font-black bg-amber-400 text-slate-950 hover:bg-amber-300">
                  {t("gift_view_vouchers", { defaultValue: "View my vouchers" })}
                  <ExternalLink size={14}/>
                </Link>
              </div>
            ) : (
              <div className="rounded-2xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-6">
                <p className="text-sm text-stone-600 dark:text-stone-300">
                  {t("gift_signup_note", {
                    defaultValue:
                      "To claim this gift, sign in (or create an account) with the email it was sent to:",
                  })}
                </p>
                <p className="mt-2 font-mono text-sm bg-white dark:bg-slate-950 rounded px-3 py-2 border border-amber-200 dark:border-amber-900"
                  data-testid="gift-recipient-email">
                  {gift.recipient_email}
                </p>
                <label className="mt-4 flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    data-testid="gift-accept-terms"
                    className="mt-1"
                  />
                  <span>
                    {t("gift_accept_terms", {
                      defaultValue:
                        "I accept the voucher terms — HustleSuite is the Merchant of Record; if I do not redeem before expiry, 90% of the paid value becomes credit at this vendor for up to 36 months.",
                    })}
                  </span>
                </label>
                <div className="mt-5 flex flex-col sm:flex-row gap-2">
                  <Button
                    disabled={!accepted || claiming}
                    onClick={claim}
                    data-testid="gift-claim-btn"
                    className="flex-1 h-12 rounded-full font-black bg-amber-400 text-slate-950 hover:bg-amber-300"
                  >
                    {claiming ? t("gift_claiming", { defaultValue: "Claiming…" })
                                : t("gift_claim_btn", { defaultValue: "Claim my gift" })}
                  </Button>
                  <Link to={`/login?next=/gifts/${code}`}
                    className="flex-1 h-12 rounded-full font-bold inline-flex items-center justify-center border border-stone-300 dark:border-stone-700 hover:border-amber-500"
                    data-testid="gift-signin"
                  >
                    {t("gift_signin_btn", { defaultValue: "Sign in / Sign up" })}
                  </Link>
                </div>
                <p className="mt-3 text-[11px] text-stone-500 flex items-center gap-1">
                  <ShieldCheck size={12} className="text-emerald-500"/>
                  {t("gift_footnote", {
                    defaultValue: "Marketplace ledger — every dollar traceable from purchaser to vendor.",
                  })}
                </p>
              </div>
            )}
          </>
        )}
      </div>
      <SuiteFooter />
    </div>
  );
}
